import React, { memo, useEffect, useState, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { string } from 'yup'
import { LockedTooltip } from 'apps/fleet-planner/components/load-details/LockedTooltip'
import { IStop } from 'apps/fleet-planner/types/loads'
import { IFacility } from 'apps/fleet-planner/utils/api'
import addressValidator from 'apps/fleet-planner/utils/createFacilityAddressValidator'
import { LOAD_FIELDS } from 'apps/fleet-planner/utils/permissions'
import { facilityByIdSelector } from 'apps/fleet-planner/utils/selectors/facilities'
import { IStore } from 'apps/fleet-planner/utils/types/store'
import { Button, Card, InlineEditContainer } from 'transfix-ui'
import { Facility } from './Facility'
import { FacilityContactInfo } from './FacilityContactInfo'
import { FacilityDetails } from './FacilityDetails'

interface IFacilityInlineEditProps {
  stop: IStop
  currentEditField?: string
  onEditModeRequested(name?: string): void
  onSubmit: () => void
  onCancel: () => void
  onStopUpdate: (stop: Partial<IStop>) => void
  sequence: number
  loading?: boolean
  error?: string
  isLocked?: boolean
}

const getDefaultState = (stop: IStop): IStop => ({
  ...stop,
  address: {
    ...stop.address,
    addressee: stop?.address?.addressee || '', // Weird situation where this is null sometimes

    // Set `id` as undefined so the stop address
    // can be updated without attempting to
    // change the `address` entity itself,
    // and just the `stop.address`
    id: undefined,
  },
})

export const FacilityInlineEdit = memo<IFacilityInlineEditProps>(
  ({
    currentEditField,
    error,
    isLocked,
    loading,
    onCancel,
    onEditModeRequested,
    onStopUpdate,
    onSubmit,
    sequence,
    stop,
  }) => {
    const defaultState = getDefaultState(stop)
    const [nextStop, setNextStop] = useState<Partial<IStop>>(defaultState)
    const [validationErrors, setValidationErrors] = useState({})
    const [isNewFacility, setIsNewFacility] = useState(false)
    const [validationMessage, setValidationMessage] = useState<string | undefined>('')

    useEffect(() => {
      const newState = getDefaultState(stop)
      // Reset with updated prop information
      setNextStop(newState)
    }, [stop])

    const validateAddress = (): boolean => {
      const requiredMessage = 'This field is required.'
      const conditionalSchema = {
        // If user provides a facility `name`, then `addressLine1` is required
        addressLine1: nextStop.facility?.name
          ? string().required(requiredMessage)
          : string().notRequired(),
        addressee: isNewFacility ? string().required(requiredMessage) : string().nullable(),
      }

      const { errors } = addressValidator.validate({ ...nextStop?.address }, conditionalSchema)
      if (errors) {
        setValidationErrors(errors)
        return !Object.keys(errors).length
      }

      setValidationErrors({})
      return true
    }

    const cancelHandler = () => {
      setNextStop(stop)
      onCancel()
    }

    const submitHandler = () => {
      const isValid = validateAddress()
      if (!isValid) return

      onStopUpdate(nextStop)
      onSubmit()
    }

    const facilityChangeHandler = (stopUpdate: Partial<IStop>, newFacility?: boolean) => {
      setNextStop(prevStop => ({
        ...prevStop,
        ...stopUpdate,
      }))

      setIsNewFacility(!!newFacility)

      if (Object.keys(validationErrors).length) {
        validateAddress()
      }
    }

    const { id } = nextStop
    const NAME = `${LOAD_FIELDS.facility}-${id}`
    const isEditMode = currentEditField === NAME
    const currentStopFacility = useSelector<IStore, IFacility | undefined>(state =>
      facilityByIdSelector(state, `${stop?.facility?.id}`)
    )
    const facilityContact = useMemo(() => {
      const { facilityContact: defaultContact } = stop || {}
      const { contacts = [] } = currentStopFacility || {}
      const [contact] = contacts
      return defaultContact || contact
    }, [stop, currentStopFacility])

    // Prevent the inline edit section from closing if there is an error.
    useEffect(() => {
      if (!error && !loading && isEditMode) onEditModeRequested('')
    }, [error, loading, onEditModeRequested])

    return (
      <InlineEditContainer
        name={NAME}
        isLoading={loading}
        editModeEnabled={isEditMode}
        resetOnClickOutside={false}
        isLocked={isLocked}
        lockedIcon={<LockedTooltip className='locked-icon' />}
        dataTest={`load-details-stop-facility-${stop.stopType}-${stop.stopSequence}`}
        editIconLocation='top'
        editMode={
          <Card className='pa4'>
            {error && (
              <div className='row pl0 mb4'>
                <InlineMessage message={error} type={INLINE_MESSAGE_VARIANTS.ALERT} />
              </div>
            )}
            <Facility
              icon='search'
              isInlineEditMode
              stop={nextStop}
              onStopChange={facilityChangeHandler}
              errors={validationErrors}
              dataTest={`inline-edit-facility-${stop.stopType}-${stop.stopSequence}`}
              onValidationChange={setValidationMessage}
            />
            <div className='tr'>
              <div className='mt4'>
                <Button color='tertiary' size='large' onClick={cancelHandler}>
                  Cancel
                </Button>
                <Button
                  color='primary'
                  size='large'
                  disabled={!!validationMessage}
                  onClick={submitHandler}>
                  Save
                </Button>
              </div>
            </div>
          </Card>
        }
        onEditModeRequested={onEditModeRequested}>
        <FacilityDetails stop={stop} sequence={sequence} />
        <FacilityContactInfo facilityContact={facilityContact} />
      </InlineEditContainer>
    )
  }
)
